import { showWarning } from 'utils/utils';
import { checkSearchCache } from 'services/proposal';
import {
  DynamicProposal,
  FlProposal,
  HotProposal,
  ProductProposal,
  Proposal,
  ReportsList,
} from 'ts/shared/interfaces/CreateProposalParams';
import { convertCurrency, dateFormat, formatPriceProposal, stripTags } from './utils';
import {
  getFullHotelInfoForProposal,
  getTripBuilderHotelDescription,
} from './hotelProposalInformation';
import { subDays } from 'date-fns';
import { availableLanguages } from 'constants/constant';

export const getReportList = (
  proposal: Proposal,
  options?: {
    pTypeBySearchToken?: Record<string, NonNullable<ProductProposal['pType']>>;
    hotelSlotKeyBySearchToken?: Record<string, string>;
    hotelImageBySlot?: Record<string, string>;
    showCancellationDate?: boolean;
    cancellationDateDelta?: number;
    language?: string;
  },
) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const descriptionLanguage =
    options?.language || resolveProposalLanguageFromBranch(user?.branch?.ext_1_pcc);
  const getPType = (searchToken: string): NonNullable<ProductProposal['pType']> =>
    options?.pTypeBySearchToken?.[searchToken] ?? 'None';
  const getPrice = (value: number, type?: 'p' | 'v' | 'e', addValue?: number) => {
    if (type === 'p' && addValue) {
      return Math.round(value * (1 + addValue / 100));
    } else if (type === 'v' && addValue) {
      return Math.round(value + addValue);
    } else if (type === 'e' && addValue) {
      return Math.round(addValue);
    }
    return Math.round(value);
  };
  const convertHotelToReport = (): ReportsList[] => {
    const hotels: ReportsList[] = proposal.hotels.map((hotel) => {
      const data = convertDynamicListOfItemsKeys(hotel.item, hotel);
      const slotKey = options?.hotelSlotKeyBySearchToken?.[hotel.searchToken];
      const slotImageUrl =
        slotKey && options?.hotelImageBySlot?.[slotKey]
          ? options.hotelImageBySlot[slotKey]
          : undefined;
      const listOfImages = slotImageUrl ? [{ code: 'HTL', name: slotImageUrl }] : undefined;

      return {
        reportName: 0,
        hotelProduct: {
          amount: data.roomClasses[0].price[hotel.currency?.toLowerCase()],
          currency: hotel.currency,
          salePrice: data.roomClasses[0].price.value,
          displayPrice: hotel.displayPrice,
          pType: getPType(hotel.searchToken),
          showCancellationDate: hotel.showCancellationDate ?? options?.showCancellationDate ?? true,
          cancellationDateDelta: hotel.cancellationDateDelta ?? options?.cancellationDateDelta ?? 0,
          listOfItemsKeys: [
            {
              hotels: [{ ...data, hotelSearchToken: hotel.searchToken }],
              dynamic: [],
              flights: [],
              nationalityCountryId: 'IL',
              ilPackages: [],
              transfers: [],
              listOfImages,
            },
          ],
        },
      };
    });

    return hotels;
  };

  const convertDynamicToReport = (): ReportsList[] => {
    const dynamic: ReportsList[] = proposal.dynamic.map((dyn) => {
      const price = getPrice(dyn.price, dyn.viewType, dyn.viewValue);

      const hotelData = convertDynamicListOfItemsKeys(dyn.hotel, dyn, true);
      return {
        reportName: 2,
        dynamicProduct: {
          amount: dyn.price,
          currency: dyn.currency,
          salePrice: price,
          displayPrice: dyn.displayPrice,
          listOfItemsKeys: [
            {
              hotels: hotelData
                ? [
                    {
                      ...hotelData,
                      displayNote: { code: 'DESC', name: dyn.freeTextHotel || '' },
                      displayOrder: dyn.displayOrder,
                    },
                  ]
                : [],
              dynamic: [
                {
                  dynamicSearchToken: dyn.searchToken,
                  displayNote: {
                    code: 'DESC',
                    name: dyn.freeTextTicket || '',
                  },
                  displayOrder: dyn.displayOrder,
                },
              ],
              flights: [
                {
                  listOfCatalogPrice: dyn.bags,
                  displayNote: {
                    code: dyn.freeTextFlight || '',
                    name: dyn.freeTextFlight || '',
                  },
                  displayOrder: dyn.displayOrder,
                },
              ],
              nationalityCountryId: 'IL',
              ilPackages: [],
              transfers: [],
            },
          ],
        },
      };
    });

    return dynamic;
  };

  const convertDynamicListOfItemsKeys = (hotel: any, body: any, isDynamic?: boolean): any => {
    if (!hotel) return null;
    const choosenRooms = hotel.roomClasses
      .filter((rc: any) => hotel.choosenRoomIds?.includes(rc.rcUniqueKey))
      .map((rc: any) => {
        return {
          ...rc,
          price: !isDynamic
            ? {
                ...rc.price,
                value: formatPriceProposal(
                  (rc.price as any)[body.currency],
                  body.currency,
                  rc.viewType || body.viewType,
                  typeof rc.viewValue === 'number' ? rc.viewValue : body.viewValue,
                ).replace(/\D/g, ''),
                originalValue: rc.price[body.currency],
                currency: body.currency,
              }
            : rc.price,
        };
      });
    const isSomeGoogleChecked =
      body.googleChecked?.reviews || body.googleChecked?.imagesIndexes.length;
    const serpHotel = !isSomeGoogleChecked
      ? null
      : {
          ...body.googleData?.serpResults.serpHotel,
          images: body.googleData?.serpResults.serpHotel.images.filter((_img: any, index: number) =>
            body.googleChecked?.imagesIndexes?.includes(index),
          ),
          reviews: body.googleChecked?.reviews
            ? body.googleData?.serpResults.serpHotel.reviews
            : null,
        };

    const isSomeTripAdvisorChecked =
      body.tripAdvisorChecked?.details ||
      body.tripAdvisorChecked?.reviews ||
      body.tripAdvisorChecked?.amenities;
    const tripAdvisorHotel = !isSomeTripAdvisorChecked
      ? null
      : {
          ...body.tripAdvisorData?.tripAdvisorHotel,
          description: body.tripAdvisorChecked?.details
            ? body.tripAdvisorData?.tripAdvisorHotel.description
            : null,
          numReviews: body.tripAdvisorChecked?.reviews
            ? body.tripAdvisorData?.tripAdvisorHotel.numReviews
            : null,
          reviewRatingCount: body.tripAdvisorChecked?.reviews
            ? body.tripAdvisorData?.tripAdvisorHotel.reviewRatingCount
            : null,
          amenities: body.tripAdvisorChecked?.amenities
            ? body.tripAdvisorData?.tripAdvisorHotel.amenities
            : null,
        };

    const hotelInformatiom = getFullHotelInfoForProposal(body.hotelInformation);

    const data = {
      hotelSearchToken: hotel.searchToken,
      hotelImageLink: hotel.info.picture,
      hotelImageDescr: hotel.images?.[0]?.desc,
      hotelAddressInfoAddress: hotel.addressInfo.address,
      hotelAddressInfoCity: hotel.addressInfo.city,
      hotelAddressInfoCountry: hotel.addressInfo.country,
      hotelAddressInfoZip: hotel.addressInfo.zip || '',
      hotelAddressInfoEmail: hotel.addressInfo.email || '',
      hotelAddressInfoPhone: hotel.addressInfo.phone || '',
      hotelItemCode: hotel.itemCode,
      hotelItemName: hotel.item.name,
      hotelLocation: hotel.location || '',
      hotelItemDURL: hotel.item.durl || '',
      hotelItemStar: hotel.item.star,
      hotelPositionLatitude: hotel.position.latitude + '',
      hotelPositionLongitude: hotel.position.longitude + '',
      roomClasses: choosenRooms,
      bpid: hotel.item.bpid,
      serpHotel,
      tripAdvisorHotel,
      hotelInformation: hotelInformatiom,
      displayNote: {
        code: 'DESC',
        name:
          (body.freeText ? body.freeText + ' ' : '') +
          getTripBuilderHotelDescription(
            {
              aI_HotelInformatiom: body.claudeData,
              tripAdvisorHotel: body.tripAdvisorHotel || body.tripAdvisorData?.tripAdvisorHotel,
              serpHotel: body.serpHotel || body.googleData?.serpResults?.serpHotel,
              hotelInformatiom: body.hotelInformation,
            },
            descriptionLanguage,
          ),
      },
      displayOrder: body.displayOrder,
      aI_HotelInformation: body.claudeData ? body.claudeData : null,
    };

    return data;
  };

  const hotels = convertHotelToReport();

  const flights: ReportsList[] = proposal.flights.map((fl) => {
    const price = getPrice(fl.price, fl.viewType, fl.viewValue);
    return {
      reportName: 1,
      flightProduct: {
        amount: fl.price,
        pType: getPType(fl.searchToken),
        currency: fl.item.flighPrice.grossAndTaxWithMarkup.currency,
        salePrice: price,
        displayPrice: fl.displayPrice,
        listOfItemsKeys: [
          {
            hotels: [],
            dynamic: [],
            flights: [
              {
                flightSearchToken: fl.searchToken,
                flightId: fl.item.id,
                listOfCatalogPrice: fl.bags,
                displayNote: {
                  code: fl.freeText || '',
                  name: fl.freeText || '',
                },
                displayOrder: fl.displayOrder,
              },
            ],
            nationalityCountryId: 'IL',
            ilPackages: [],
            transfers: [],
          },
        ],
      },
    };
  });

  const transfers: ReportsList[] = proposal.transfers.map((tran) => {
    const price = getPrice(tran.price, tran.viewType, tran.viewValue);
    return {
      reportName: 5,
      transferProduct: {
        amount: tran.price,
        currency: tran.currency,
        salePrice: price,
        displayPrice: tran.displayPrice,
        listOfItemsKeys: [
          {
            hotels: [],
            dynamic: [],
            flights: [],
            nationalityCountryId: 'IL',
            ilPackages: [],
            transfers: [
              {
                transferSearchToken: tran.searchToken,
                ukeyForward: tran.forwardItem?.priceKey || '',
                ukeyReturn: tran.returnItem?.priceKey,
                displayNote: {
                  code: 'DESC',
                  name: tran.freeText || '',
                },
                displayOrder: tran.displayOrder,
              },
            ],
          },
        ],
      },
    };
  });

  const packages: ReportsList[] = proposal.ilPackages.map((pac) => {
    const price = getPrice(pac.price, pac.viewType, pac.viewValue);
    return {
      reportName: 8,
      ilPackageProduct: {
        amount: pac.price,
        currency: pac.currency,
        salePrice: price,
        displayPrice: pac.displayPrice,
        listOfItemsKeys: [
          {
            hotels: [],
            dynamic: [],
            flights: [],
            nationalityCountryId: 'IL',
            ilPackages: [
              {
                queryClient: {
                  searchToken: pac.searchToken,
                  dealId: pac.item.deal.dealId,
                  rowId: pac.item.deal.rowId,
                  hotelId: pac.item.deal.hotelId,
                  type: pac.type,
                  paxComplect: pac.item.deal.paxComplect,
                  provider: pac.item.deal.provider,
                  cityCode:
                    pac.item.info.hotelLocation?.cityCode || pac.item.deal.flightDestination || '',
                  checkIn: pac.item.deal.departureDate.split('T')[0],
                  name: pac.item.info.hotelName || pac.item.deal.rowName,
                  isSale: false,
                  selectedRooms: pac.selectedRooms,
                  selectedInfantInRooms: pac.selectedInfantInRooms,
                  rooms: pac.rooms,
                },
                displayNote: {
                  code: 'DESC',
                  name: pac.freeText || '',
                },
                displayOrder: pac.displayOrder,
              },
            ],
            transfers: [],
          },
        ],
      },
    };
  });

  const dynamic = convertDynamicToReport();

  return [...hotels, ...flights, ...transfers, ...packages, ...dynamic];
};

export const formatFltPersons = (flight: any, t: any) => {
  let resultArr: string[] = [];

  let numberOfAdults = 0;
  let numberOfChildren = 0;
  let numberOfInfants = 0;
  let numberOfYouths = 0;
  let numberOfSeniors = 0;

  if (flight.flightPricesByPDT) {
    numberOfAdults = flight.flightPricesByPDT.filter((pdt: any) => pdt.paxPDT === 'ADT').length;
    numberOfChildren = flight.flightPricesByPDT.filter((pdt: any) => pdt.paxPDT === 'CHD').length;
    numberOfInfants = flight.flightPricesByPDT.filter((pdt: any) => pdt.paxPDT === 'INF').length;
    numberOfYouths = flight.flightPricesByPDT.filter((pdt: any) => pdt.paxPDT === 'YTH').length;
    numberOfSeniors = flight.flightPricesByPDT.filter((pdt: any) => pdt.paxPDT === 'SRC').length;
  } else if (flight.pax) {
    numberOfAdults = flight.pax.adults;
    numberOfChildren = flight.pax.children;
    numberOfInfants = flight.pax.infant;
    numberOfYouths = flight.pax.youth;
    numberOfSeniors = flight.pax.senior;
  }

  if (numberOfAdults) {
    const adultStr = numberOfAdults > 1 ? t('search.adults') : t('search.adult');
    resultArr.push(`${numberOfAdults} ${adultStr}`);
  }

  if (numberOfChildren) {
    const childrenStr = numberOfChildren > 1 ? t('search.children') : t('search.child');
    resultArr.push(`${numberOfChildren} ${childrenStr}`);
  }

  if (numberOfYouths) {
    const youthsStr = numberOfYouths > 1 ? t('search.youths') : t('search.youth');
    resultArr.push(`${numberOfYouths} ${youthsStr}`);
  }

  if (numberOfSeniors) {
    const seniorsStr = numberOfSeniors > 1 ? t('search.seniors') : t('search.senior');
    resultArr.push(`${numberOfSeniors} ${seniorsStr}`);
  }

  if (numberOfInfants) {
    const infantsStr = numberOfInfants > 1 ? t('search.infants') : t('search.infant');
    resultArr.push(`${numberOfInfants} ${infantsStr}`);
  }

  return resultArr.join(', ');
};

export const checkIfProposalExpired = async (proposal: Proposal, t: any) => {
  const listOfProducts: Record<string, string> = {};

  proposal.hotels.forEach((hotel) => {
    listOfProducts[hotel.searchToken] = 'HotelProposal';
  });

  proposal.flights.forEach((flight) => {
    listOfProducts[flight.searchToken] = 'FlightProposal';
  });

  proposal.dynamic.forEach((dynamic) => {
    listOfProducts[dynamic.searchToken] = 'DynamicProposal';
  });

  proposal.transfers.forEach((transfer) => {
    listOfProducts[transfer.searchToken] = 'TransferProposal';
  });

  proposal.ilPackages.forEach((ilPackage) => {
    listOfProducts[ilPackage.searchToken] = 'ILPackages';
  });

  const response = await checkSearchCache(listOfProducts);
  if (response) {
    const expiredKeys = Object.keys(response).filter((key) => response[key] === false);
    if (expiredKeys.length) {
      showWarning(t('proposal.some_of_the_products_were_expired'));
      const expiredProducts = expiredKeys.map((key) => listOfProducts[key]);

      if (expiredProducts.includes('HotelProposal')) {
        const newProposal = { ...proposal };
        newProposal.hotels = newProposal.hotels.filter(
          (hotel) => !expiredKeys.includes(hotel.searchToken),
        );
        return newProposal;
      }
      if (expiredProducts.includes('FlightProposal')) {
        const newProposal = { ...proposal };
        newProposal.flights = newProposal.flights.filter(
          (flight) => !expiredKeys.includes(flight.searchToken),
        );
        return newProposal;
      }
      if (expiredProducts.includes('DynamicProposal')) {
        const newProposal = { ...proposal };
        newProposal.dynamic = newProposal.dynamic.filter(
          (dynamic) => !expiredKeys.includes(dynamic.searchToken),
        );
        return newProposal;
      }
      if (expiredProducts.includes('TransferProposal')) {
        const newProposal = { ...proposal };
        newProposal.transfers = newProposal.transfers.filter(
          (transfer) => !expiredKeys.includes(transfer.searchToken),
        );
        return newProposal;
      }
      if (expiredProducts.includes('ILPackages')) {
        const newProposal = { ...proposal };
        newProposal.ilPackages = newProposal.ilPackages.filter(
          (ilPackage) => !expiredKeys.includes(ilPackage.searchToken),
        );
        return newProposal;
      }
    }
  }
};

export const isValidProposalCancellationDate = (cxl: string) =>
  new Date(cxl) > new Date(2000, 0, 1);

export const getAdjustedProposalCancellationDate = (cxl: string, deltaDays: number) =>
  subDays(new Date(cxl), deltaDays);

export const getProposalLanguageFallback = () => {
  const initSiteData = JSON.parse(sessionStorage.getItem('initSiteData') || '{}');
  const nationality = initSiteData.other?.defaultNationality?.toLowerCase();
  return nationality === 'it' ? 'it' : 'he';
};

export const resolveProposalLanguageFromBranch = (ext1Pcc: string | undefined) => {
  const initSiteData = JSON.parse(localStorage.getItem('initSiteData') || '{}');
  const nationality = initSiteData.other?.defaultNationality?.toLowerCase();
  const fallback = nationality === 'it' ? 'it' : 'he';

  if (!ext1Pcc) {
    return fallback;
  }
  for (const part of ext1Pcc.split(',')) {
    const colonIndex = part.indexOf(':');
    if (colonIndex === -1 || part.slice(0, colonIndex).trim().toUpperCase() !== 'PROPOSAL_LANG') {
      continue;
    }
    const proposalLang = part
      .slice(colonIndex + 1)
      .trim()
      .toLowerCase();
    return availableLanguages.some((lang) => lang.lang === proposalLang) ? proposalLang : fallback;
  }
  return fallback;
};

export const getFirstProposalProductCurrency = (proposal: Proposal): string => {
  const product =
    proposal.hotels[0] ||
    proposal.flights[0] ||
    proposal.dynamic[0] ||
    proposal.transfers[0] ||
    proposal.ilPackages[0] ||
    proposal.existingProposal?.listOfProducts?.[0];

  return (product?.currency || 'usd').toLowerCase();
};

export const getActiveProposalCurrency = (proposal: Proposal): string =>
  sessionStorage.getItem('displayCurrencyHotels') || getFirstProposalProductCurrency(proposal);

export const hasProposalProducts = (proposal: Proposal): boolean =>
  !!(
    proposal.hotels.length ||
    proposal.flights.length ||
    proposal.dynamic.length ||
    proposal.transfers.length ||
    proposal.ilPackages.length ||
    proposal.existingProposal?.listOfProducts?.length
  );

const getHotelPriceInCurrency = (hotel: HotProposal, currency: string): number => {
  const roomId = hotel.item.choosenRoomIds?.[0] || hotel.item.choosenRoomId;
  const room =
    hotel.item.roomClasses?.find((rc: any) => rc.rcUniqueKey === roomId) ||
    hotel.item.roomClasses?.[0];

  if (!room?.price) {
    return hotel.price;
  }

  return (room.price as any)[currency as keyof any] ?? room.price.usd ?? hotel.price;
};

const getFlightBagsPriceInCurrency = (flight: FlProposal, currency: string): number =>
  flight.bags?.reduce((acc: number, bag: any) => {
    const bagPrice = (bag.displayPrice as any)?.[currency.toLowerCase()];
    return acc + (typeof bagPrice === 'number' ? bagPrice : bag.price || 0);
  }, 0) || 0;

const getFlightPriceInCurrency = (flight: FlProposal, currency: string): number => {
  const currencyKey = currency.toLowerCase();
  const gross = flight.item?.flighPrice?.grossAndTaxWithMarkup;
  const bagsPrice = getFlightBagsPriceInCurrency(flight, currencyKey);

  if (gross) {
    const multiCurrencyPrice = (gross as any)[currencyKey];
    if (typeof multiCurrencyPrice === 'number' && multiCurrencyPrice > 0) {
      return multiCurrencyPrice + bagsPrice;
    }

    if (currencyKey === 'thb' && gross.usd) {
      const thbUsdRate = parseFloat(sessionStorage.getItem('thbUsdRate') || '1');
      if (thbUsdRate > 0) {
        return gross.usd / thbUsdRate + bagsPrice;
      }
    }

    if (gross.currency?.toLowerCase() === currencyKey) {
      return gross.value + bagsPrice;
    }
  }

  return flight.price;
};

const getRoomPriceSource = (roomClass: any, hotel: HotProposal) => {
  const fromCurrency = (roomClass.price?.currency || hotel.currency || 'usd').toLowerCase();
  const fromPrice =
    (roomClass.price as any)?.[fromCurrency as keyof any] ?? roomClass.price?.value ?? hotel.price;

  return { fromCurrency, fromPrice };
};

const convertHotelProposalCurrency = async (
  hotel: HotProposal,
  currency: string,
  convertPrice: (price: number, fromCurrency?: string) => Promise<number>,
): Promise<HotProposal> => {
  const roomClasses = await Promise.all(
    (hotel.item.roomClasses || []).map(async (roomClass: any) => {
      const targetPrice = (roomClass.price as any)?.[currency as keyof any];
      if (typeof targetPrice === 'number') {
        return roomClass;
      }

      const { fromCurrency, fromPrice } = getRoomPriceSource(roomClass, hotel);
      const convertedPrice =
        fromCurrency === currency ? fromPrice : await convertPrice(fromPrice, fromCurrency);

      return {
        ...roomClass,
        price: {
          ...roomClass.price,
          value: convertedPrice,
          currency,
          [currency]: convertedPrice,
        },
      };
    }),
  );

  const updatedHotel: HotProposal = {
    ...hotel,
    currency,
    item: {
      ...hotel.item,
      roomClasses,
    },
    price: 0,
  };

  updatedHotel.price = getHotelPriceInCurrency(updatedHotel, currency);

  return updatedHotel;
};

const convertExistingProposalProducts = async (
  products: any[],
  currency: string,
  convertPrice: (price: number, fromCurrency?: string) => Promise<number>,
) =>
  Promise.all(
    products.map(async (product) => {
      const listOfItems = await Promise.all(
        (product.listOfItems || []).map(async (item: any) => {
          const hotels = await Promise.all(
            (item.hotels || []).map(async (hotel: any) => {
              const rooms = await Promise.all(
                (hotel.rooms || []).map(async (room: any) => {
                  const supplement = room.supplements?.[0];
                  if (!supplement?.code) {
                    return room;
                  }

                  const roomPrice = +supplement.code;
                  if (!Number.isFinite(roomPrice) || roomPrice <= 0) {
                    return room;
                  }

                  const roomCurrency = supplement.name || product.currency;
                  const convertedPrice = await convertPrice(roomPrice, roomCurrency);

                  return {
                    ...room,
                    supplements: [
                      {
                        ...supplement,
                        code: String(Math.round(convertedPrice)),
                        name: currency,
                      },
                      ...(room.supplements?.slice(1) || []),
                    ],
                  };
                }),
              );

              return {
                ...hotel,
                rooms,
              };
            }),
          );

          return {
            ...item,
            hotels,
          };
        }),
      );

      const previousCurrency = (product.currency || 'usd').toLowerCase();
      const currencyChanged = previousCurrency !== currency;

      return {
        ...product,
        currency,
        amount: await convertPrice(product.amount, product.currency),
        salePrice: await convertPrice(product.salePrice, product.currency),
        listOfItems,
        edited: product.edited || currencyChanged,
      };
    }),
  );

export const applyProposalCurrency = async (
  proposal: Proposal,
  newCurrency: string,
): Promise<Proposal> => {
  const currency = newCurrency.toLowerCase();

  const convertPrice = async (price: number, fromCurrency?: string) => {
    if (!fromCurrency || fromCurrency.toLowerCase() === currency) {
      return price;
    }

    const converted = await convertCurrency(price, fromCurrency, currency);

    return converted ?? price;
  };

  const hotels = await Promise.all(
    proposal.hotels.map((hotel) => convertHotelProposalCurrency(hotel, currency, convertPrice)),
  );

  const flights = await Promise.all(
    proposal.flights.map(async (flight) => {
      let price = getFlightPriceInCurrency(flight, currency);

      if (flight.currency?.toLowerCase() !== currency && price === flight.price) {
        price = await convertPrice(flight.price, flight.currency);
      }

      return {
        ...flight,
        currency,
        price,
      };
    }),
  );

  const dynamic = await Promise.all(
    proposal.dynamic.map(async (item) => {
      let price = item.price;

      if (item.attraction && !item.hotel && !item.flight) {
        const grossPrice = (item.attraction?.price?.gross as any)?.[currency];

        if (typeof grossPrice === 'number') {
          price = grossPrice;
        } else {
          price = await convertPrice(item.price, item.currency);
        }
      } else {
        price = await convertPrice(item.price, item.currency);
      }

      return {
        ...item,
        currency,
        price,
      } as DynamicProposal;
    }),
  );

  const transfers = await Promise.all(
    proposal.transfers.map(async (transfer) => ({
      ...transfer,
      currency,
      price: await convertPrice(transfer.price, transfer.currency),
    })),
  );

  const ilPackages = await Promise.all(
    proposal.ilPackages.map(async (pac) => ({
      ...pac,
      currency,
      price: await convertPrice(pac.price, pac.currency),
    })),
  );

  let existingProposal = proposal.existingProposal;

  if (existingProposal?.listOfProducts?.length) {
    const listOfProducts = await convertExistingProposalProducts(
      existingProposal.listOfProducts,
      currency,
      convertPrice,
    );

    existingProposal = {
      ...existingProposal,
      listOfProducts,
    };
  }

  return {
    ...proposal,
    hotels,
    flights,
    dynamic,
    transfers,
    ilPackages,
    existingProposal,
  };
};

export const getProposalDescription = (proposal: Proposal) => {
  if (proposal.existingProposal?.description) {
    return stripTags(proposal.existingProposal.description);
  }

  if (proposal.hotels?.length) {
    const hotel = proposal.hotels[0].item;
    return stripTags(
      `${hotel.item.name} - ${dateFormat(hotel.roomClasses?.[0]?.checkIn, 'dd MMM yyyy')}`,
    );
  }

  if (proposal.flights?.length) {
    const flightBounds = proposal.flights[0].item?.flightBounds;
    const flight = proposal.flights[0].item?.inbound || flightBounds?.[0];
    if (flight) {
      return stripTags(
        `${flight.departureAirport.cityDesc} - ${dateFormat(flight.departureDateTime, 'dd MMM yyyy')}`,
      );
    }
  }

  if (proposal.dynamic?.length) {
    const dynamic = proposal.dynamic[0].hotel;
    const attraction = proposal.dynamic[0].attraction;
    if (dynamic) {
      return stripTags(
        `${dynamic.item.name} - ${dateFormat(dynamic.roomClasses?.[0]?.checkIn, 'dd MMM yyyy')}`,
      );
    }
    if (attraction) {
      return stripTags(
        `${attraction.showName} - ${dateFormat(attraction.eventDate, 'dd MMM yyyy', 'he')}`,
      );
    }
  }

  if (proposal.transfers?.length) {
    const transfer = proposal.transfers[0].forwardItem?.transfer;
    if (transfer) {
      const desc = transfer.pickUpDetails?.address || transfer.pickUpDetails?.airport.desc;
      return stripTags(
        `${desc} - ${dateFormat(transfer.pickUpDetails?.pickUpDateTime, 'dd MMM yyyy')}`,
      );
    }
  }

  if (proposal.ilPackages?.length) {
    const ilPackage = proposal.ilPackages[0].item;
    if (ilPackage) {
      const date = ilPackage.deal.departureDate;
      return stripTags(
        `${ilPackage.info.hotelName || ilPackage.deal.rowName} - ${dateFormat(date, 'dd MMM yyyy')}`,
      );
    }
  }

  return 'Proposal';
};
